import { add, isAfter, startOfWeek, startOfDay } from '@chilipiper/date-time'
import {
  MultipleRoundRobinMeetingStartTimes,
  UserAvailabilityApiClientV1,
  UsersCombinedMeetingStartTimes,
} from '@chilipiper/api-client/src/user-availability'
import invariant from 'tiny-invariant'
import {
  GetAvailabilityOptions,
  RequestAvailabilityOptions,
  UserRequestAvailability,
  MultiRoundRobinRequestAvailability,
  IntervalOptions,
  UsersAvailability,
  MultiRoundRobinAvailability,
  isUserExpectedHost,
} from '../../load-availability-options'
import { getInitialInterval } from '../helpers'
import { AvailabilitySpan, BaseRequest, BuildBaseRequest } from '../types'

const DEFAULT_INTERVAL_DURATION = '7 days'

type AvailabilityResponse = UsersCombinedMeetingStartTimes | MultipleRoundRobinMeetingStartTimes

type ExecuteFunction<T extends GetAvailabilityOptions = GetAvailabilityOptions> = (
  params: RequestAvailabilityOptions & T
) => Promise<AvailabilityResponse>

type AvailabilityStrategies = readonly [
  AvailabilityStrategy<UserRequestAvailability>,
  AvailabilityStrategy<MultiRoundRobinRequestAvailability>,
]

type InferStrategyType<T> = T extends AvailabilityStrategy<infer U> ? U : never

type AvailabilityStrategy<T extends GetAvailabilityOptions> = {
  predicate: (props: GetAvailabilityOptions) => props is T
  execute: (
    userAvailabilityClient: UserAvailabilityApiClientV1,
    tenantId: string,
    params: BaseRequest & T
  ) => Promise<AvailabilityResponse>
}

export type GetAvailability = GetAvailabilityOptions & Partial<IntervalOptions>

const buildRequestBase = ({
  intervalDuration,
  intervalStartsAt,
  availabilityEndsAt,
  meetingTypeId,
  meetingTypeVersion = new Date().toISOString(),
  afterBufferDurationOverride,
  beforeBufferDurationOverride,
  meetingDurationOverride,
}: BuildBaseRequest): BaseRequest => {
  return {
    availabilityEndsAt,
    intervalStartsAt,
    interval: {
      duration: intervalDuration,
      startsAt: intervalStartsAt,
    },
    meetingTypeRef: {
      id: meetingTypeId,
      timestamp: meetingTypeVersion,
    },
    meetingTypeOverride: {
      meetingDurationOverride: meetingDurationOverride,
      beforeBufferDurationOverride: beforeBufferDurationOverride,
      afterBufferDurationOverride: afterBufferDurationOverride,
    },
  }
}

const withPagination = <T extends GetAvailabilityOptions>(
  getAvailabilityFn: ExecuteFunction<T>,
  props: RequestAvailabilityOptions & T
) => {
  return async (response: AvailabilityResponse): Promise<AvailabilityResponse> => {
    const newIntervalStartsAt = add(startOfWeek(new Date(props.intervalStartsAt)), { weeks: 1 })
    const reachedEndOfAvailability = isAfter(
      newIntervalStartsAt,
      new Date(props.availabilityEndsAt)
    )

    if (
      (response.startTimes ?? []).length > 0 ||
      reachedEndOfAvailability ||
      (props.loadedWeek ?? 1) > 1
    ) {
      return response
    }

    const nextPageResponse = await getAvailabilityFn({
      ...props,
      interval: {
        startsAt: newIntervalStartsAt.toISOString(),
        duration: props.intervalDuration ?? DEFAULT_INTERVAL_DURATION,
      },
      intervalStartsAt: newIntervalStartsAt.toISOString(),
      intervalDuration: props.intervalDuration ?? DEFAULT_INTERVAL_DURATION,
      availabilityEndsAt: props.availabilityEndsAt,
    })

    return withPagination(getAvailabilityFn, {
      ...props,
      intervalStartsAt: newIntervalStartsAt.toISOString(),
      intervalDuration: props.intervalDuration ?? DEFAULT_INTERVAL_DURATION,
    })(nextPageResponse)
  }
}

const createExecuteWrapper = <T extends GetAvailabilityOptions>(
  execute: AvailabilityStrategy<T>['execute'],
  tenantId: string,
  userAvailabilityClient: UserAvailabilityApiClientV1
): ExecuteFunction<T> => {
  return (params: RequestAvailabilityOptions & T) =>
    execute(userAvailabilityClient, tenantId, params as unknown as BaseRequest & T)
}

const findUsersAvailability = (
  userAvailabilityClient: UserAvailabilityApiClientV1,
  tenantId: string,
  params: BaseRequest & UsersAvailability
): Promise<UsersCombinedMeetingStartTimes> => {
  invariant(
    isUserExpectedHost(params.expectedHost),
    'Expected host is required for combined availability'
  )

  return userAvailabilityClient.getUsersAvailabilities(
    tenantId,
    {
      interval: params.interval,
      meetingTypeOverride: params.meetingTypeOverride,
      meetingTypeRef: params.meetingTypeRef,
      meetingLimit: params.meetingLimit ?? { type: 'NoMeetingLimit' },
      userIds: [...params.userIds, ...(params.additionalRequiredAttendees ?? [])],
      expectedHost: params.expectedHost.userId,
    },
    { feature: params.feature ? [params.feature] : undefined }
  )
}

const findMultiRoundRobinAvailability = (
  userAvailabilityClient: UserAvailabilityApiClientV1,
  tenantId: string,
  params: BaseRequest & MultiRoundRobinAvailability
): Promise<MultipleRoundRobinMeetingStartTimes> => {
  return userAvailabilityClient.getAvailabilitiesForMultipleRoundRobin(
    tenantId,
    {
      interval: params.interval,
      meetingTypeOverride: params.meetingTypeOverride,
      meetingTypeRef: params.meetingTypeRef,
      additionalRequiredAttendeesOpt: params.additionalRequiredAttendees?.length
        ? params.additionalRequiredAttendees
        : undefined,
      distributionAttendees: params.distributionAttendees,
      expectedHost: params.expectedHost,
    },
    { feature: params.feature ? [params.feature] : undefined }
  )
}

const usersStrategy: AvailabilityStrategy<UserRequestAvailability> = {
  predicate: (props): props is UserRequestAvailability => 'userIds' in props,
  execute: findUsersAvailability,
}

const multiRoundRobinStrategy: AvailabilityStrategy<MultiRoundRobinRequestAvailability> = {
  predicate: (props): props is MultiRoundRobinRequestAvailability =>
    'distributionAttendees' in props,
  execute: findMultiRoundRobinAvailability,
}

const availabilityStrategies: AvailabilityStrategies = [
  usersStrategy,
  multiRoundRobinStrategy,
] as const

export const getAvailability = async (
  props: GetAvailability & {
    availabilitySpan?: AvailabilitySpan
    userAvailabilityClient: UserAvailabilityApiClientV1
  }
) => {
  if (
    !(
      props.availabilitySpan ||
      (props.intervalStartsAt && props.intervalDuration && props.availabilityEndsAt)
    )
  ) {
    return Promise.resolve({
      timeSlots: {
        meetingTypeRef: {
          id: props.meetingTypeId,
          timestamp: props.meetingTypeVersion,
        },
        startTimes: [],
      } as AvailabilityResponse,
    })
  }
  const baseRequest = buildRequestBase({
    intervalDuration:
      props.intervalDuration ?? getInitialInterval(props.availabilitySpan?.startsAt as string),
    intervalStartsAt:
      props.intervalStartsAt ??
      startOfDay(new Date(props.availabilitySpan?.startsAt as string)).toISOString(),
    availabilityEndsAt: props.availabilityEndsAt ?? (props.availabilitySpan?.endsAt as string),
    meetingTypeId: props.meetingTypeId,
    meetingTypeVersion: props.meetingTypeVersion,
    ...props.meetingTypeOverride,
  })

  const getTimeSlots = async () => {
    try {
      const strategy = availabilityStrategies.find(strategy => strategy.predicate(props))
      if (!strategy) {
        throw new Error('Invalid availability type')
      }
      type StrategyType = InferStrategyType<typeof strategy>

      const executeWithParams = createExecuteWrapper<StrategyType>(
        strategy.execute as (
          userAvailabilityClient: UserAvailabilityApiClientV1,
          tenantId: string,
          params: BaseRequest & StrategyType
        ) => Promise<AvailabilityResponse>,
        props.tenantId,
        props.userAvailabilityClient
      )

      const requestArgs = {
        ...baseRequest,
        ...props,
        userAvailabilityClient: props.userAvailabilityClient,
      } satisfies RequestAvailabilityOptions &
        StrategyType & { userAvailabilityClient: UserAvailabilityApiClientV1 }

      const response = await executeWithParams(requestArgs)
      return withPagination(executeWithParams, requestArgs)(response)
    } catch (error) {
      return {
        meetingTypeRef: {
          id: props.meetingTypeId,
          timestamp: props.meetingTypeVersion || new Date().toISOString(),
        },
        startTimes: [],
      } satisfies AvailabilityResponse
    }
  }

  return {
    timeSlots: await getTimeSlots(),
  }
}
